// Validation utilities for Vendor Due Diligence forms

export interface ValidationError {
  field: string;
  message: string;
}

export interface BasicDetailsData {
  estimatedContractAmount: string;
  requestorName: string;
  requestorManager: string;
  companyName: string;
  streetAddress: string;
  addressLine2: string; // Optional
  city: string;
  zipCode: string;
  state: string;
  country: string;
  phone: string;
  contactPerson: string;
  contactEmail: string;
  vendorWebsite: string;
  howLearnedAbout: string;
  goodsServicesDescription: string;
  needsContractorAgreement?: boolean;
}

export interface ThirdPartyRiskData {
  ofacSanctions: string;
  ofacSanctionsDetails: string;
  angelRelationships: string;
  angelRelationshipsDetails: string;
  personalInfoAccess: string;
  personalInfoDetails: string;
  specialCircumstances: string;
  specialCircumstancesDetails: string;
  paymentTerms?: string;
  projectDeliverables?: string;
  freeFromControl?: string;
  workOutsideOfNormalBusiness?: string;
  independentlyEstablishedBusiness?: string;
  effectiveDate?: string;
  expirationDate?: string;
}

export interface CommercialDueDiligenceData {
  reputationalConcerns: string;
  reputationalConcernsDetails: string;
  searchTermsTools: string;
  experienceCredentials: string;
}

export interface BidsData {
  competitiveBidsObtained: string;
  bid1Description: string;
  bid1Total: string;
  bid2Description: string;
  bid2Total: string;
  bid3Description: string;
  bid3Total: string;
  selectionReason: string;
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate zip code format (supports formats like 12345 or 12345-6789)
 */
function isValidZipCode(zip: string): boolean {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
}

/**
 * Validate numeric value (allows decimals)
 */
function isValidNumber(value: string): boolean {
  const numRegex = /^\d+(\.\d+)?$/;
  return numRegex.test(value.trim());
}

/**
 * Validate Form A: Basic Details
 * Everything is required except addressLine2
 */
export function validateBasicDetails(data: BasicDetailsData, needsContractorAgreement: boolean = false): ValidationError[] {
  const errors: ValidationError[] = [];
  const amount = parseFloat(data.estimatedContractAmount) || 0;

  // Estimated Contract Amount - required and must be a valid number
  if (!data.estimatedContractAmount || data.estimatedContractAmount.trim() === '') {
    errors.push({ field: 'estimatedContractAmount', message: 'Estimated contract amount is required' });
  } else if (!isValidNumber(data.estimatedContractAmount)) {
    errors.push({ field: 'estimatedContractAmount', message: 'Estimated contract amount must be a valid number' });
  }

  // Always required fields
  const alwaysRequiredFields: (keyof BasicDetailsData)[] = [
    'requestorName',
    'requestorManager',
    'companyName',
    'streetAddress',
    'city',
    'state',
    'country',
    'phone',
    'contactPerson',
    'goodsServicesDescription',
  ];

  for (const field of alwaysRequiredFields) {
    const value = data[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      const fieldName = field.replace(/([A-Z])/g, ' $1').toLowerCase();
      errors.push({ field, message: `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required` });
    }
  }

  // Zip Code - required and must be valid format
  if (!data.zipCode || data.zipCode.trim() === '') {
    errors.push({ field: 'zipCode', message: 'Zip code is required' });
  } else if (!isValidZipCode(data.zipCode)) {
    errors.push({ field: 'zipCode', message: 'Zip code must be in format 12345 or 12345-6789' });
  }

  // Contact Email - required and must be valid email
  if (!data.contactEmail || data.contactEmail.trim() === '') {
    errors.push({ field: 'contactEmail', message: 'Contact email is required' });
  } else if (!isValidEmail(data.contactEmail)) {
    errors.push({ field: 'contactEmail', message: 'Contact email must be a valid email address' });
  }

  // Accounting-only fields (required when amount >= $1000)
  if (amount >= 1000) {
    if (!data.vendorWebsite || data.vendorWebsite.trim() === '') {
      errors.push({ field: 'vendorWebsite', message: 'Vendor website is required (enter N/A if not applicable)' });
    }
    
    if (!data.howLearnedAbout || data.howLearnedAbout.trim() === '') {
      errors.push({ field: 'howLearnedAbout', message: 'How did you learn about this vendor is required' });
    }
  }

  return errors;
}

/**
 * Validate Form B: Third Party Risk Profile Checklist
 * Corrected conditional logic per user table:
 * - Accounting (OFAC, personal data, special circumstances): ALWAYS validated
 * - Familial relationships: validated when showFamilial = true (amount >= $1k)
 * - Ironclad (payment terms, deliverables, 3 independence questions, contract dates): validated when needsIronclad = true (needsContract)
 * - Detail fields: Required when parent question is "YES"
 */
export function validateThirdPartyRisk(data: ThirdPartyRiskData, needsIronclad: boolean = false, showFamilial: boolean = false): ValidationError[] {
  const errors: ValidationError[] = [];

  // ACCOUNTING: OFAC, Personal Data, Special Circumstances - ALWAYS validated
  if (!data.ofacSanctions) {
    errors.push({ field: 'ofacSanctions', message: 'Please answer the OFAC sanctions question' });
  } else if (data.ofacSanctions === 'YES' && (!data.ofacSanctionsDetails || data.ofacSanctionsDetails.trim() === '')) {
    errors.push({ field: 'ofacSanctionsDetails', message: 'Details are required when OFAC sanctions answer is YES' });
  }
  
  if (!data.personalInfoAccess) {
    errors.push({ field: 'personalInfoAccess', message: 'Please answer the personal data access question' });
  } else if (data.personalInfoAccess === 'YES' && (!data.personalInfoDetails || data.personalInfoDetails.trim() === '')) {
    errors.push({ field: 'personalInfoDetails', message: 'Details are required when personal data access answer is YES' });
  }
  
  if (!data.specialCircumstances) {
    errors.push({ field: 'specialCircumstances', message: 'Please answer the special circumstances question' });
  } else if (data.specialCircumstances === 'YES' && (!data.specialCircumstancesDetails || data.specialCircumstancesDetails.trim() === '')) {
    errors.push({ field: 'specialCircumstancesDetails', message: 'Details are required when special circumstances answer is YES' });
  }

  // BOTH: Familial relationships - validated when amount >= $1k
  if (showFamilial) {
    if (!data.angelRelationships) {
      errors.push({ field: 'angelRelationships', message: 'Please answer the familial relationships question' });
    } else if (data.angelRelationships === 'YES' && (!data.angelRelationshipsDetails || data.angelRelationshipsDetails.trim() === '')) {
      errors.push({ field: 'angelRelationshipsDetails', message: 'Details are required when familial relationships answer is YES' });
    }
  }

  // IRONCLAD ONLY: Payment Terms, Deliverables, 3 Contractor Independence Questions, Contract Dates
  if (needsIronclad) {
    if (!data.paymentTerms || data.paymentTerms.trim() === '') {
      errors.push({ field: 'paymentTerms', message: 'Payment terms are required' });
    }
    
    if (!data.projectDeliverables || data.projectDeliverables.trim() === '') {
      errors.push({ field: 'projectDeliverables', message: 'Expected project deliverables are required' });
    }
    
    if (!data.freeFromControl) {
      errors.push({ field: 'freeFromControl', message: 'Please answer the independent contractor question' });
    }
    
    if (!data.workOutsideOfNormalBusiness) {
      errors.push({ field: 'workOutsideOfNormalBusiness', message: 'Please answer the work outside normal business question' });
    }
    
    if (!data.independentlyEstablishedBusiness) {
      errors.push({ field: 'independentlyEstablishedBusiness', message: 'Please answer the independently established business question' });
    }
    
    if (!data.effectiveDate || data.effectiveDate.trim() === '') {
      errors.push({ field: 'effectiveDate', message: 'Contract start date is required' });
    }
    
    if (!data.expirationDate || data.expirationDate.trim() === '') {
      errors.push({ field: 'expirationDate', message: 'Contract end date is required' });
    }
  }

  return errors;
}

/**
 * Validate Form C: Commercial Due Diligence
 * All fields are required when this form is shown
 */
export function validateCommercialDueDiligence(
  data: CommercialDueDiligenceData,
  estimatedContractAmount: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Reputational Concerns - Required
  if (!data.reputationalConcerns?.trim()) {
    errors.push({ field: 'reputationalConcerns', message: 'Please answer whether you identified fraud, litigation, or reputational concerns' });
  } else if (data.reputationalConcerns === 'YES' && !data.reputationalConcernsDetails?.trim()) {
    errors.push({ field: 'reputationalConcernsDetails', message: 'Details are required when reputational concerns answer is YES' });
  }

  // Search terms/tools (ALWAYS required when this form is shown)
  if (!data.searchTermsTools?.trim()) {
    errors.push({ field: 'searchTermsTools', message: 'Details of your review are required' });
  }

  // Experience/Credentials (ALWAYS required)
  if (!data.experienceCredentials?.trim()) {
    errors.push({ field: 'experienceCredentials', message: 'Explanation of vendor experience and credentials is required' });
  }

  return errors;
}

/**
 * Validate Form D: Competitive Bidding
 */
export function validateBids(data: BidsData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate competitive bids question
  if (!data.competitiveBidsObtained?.trim()) {
    errors.push({ field: 'competitiveBidsObtained', message: 'Please answer whether competitive bids were obtained' });
    return errors;
  }

  if (data.competitiveBidsObtained === 'YES') {
    // Require Bid 1 and Bid 2 (Bid 3 is optional)
    if (!data.bid1Description?.trim()) {
      errors.push({ field: 'bid1Description', message: 'Bid 1 description is required when competitive bids were obtained' });
    }
    if (!data.bid1Total?.trim()) {
      errors.push({ field: 'bid1Total', message: 'Bid 1 total is required when competitive bids were obtained' });
    }
    if (!data.bid2Description?.trim()) {
      errors.push({ field: 'bid2Description', message: 'Bid 2 description is required when competitive bids were obtained' });
    }
    if (!data.bid2Total?.trim()) {
      errors.push({ field: 'bid2Total', message: 'Bid 2 total is required when competitive bids were obtained' });
    }
  }

  // Box 4 (selectionReason) is always required
  if (!data.selectionReason?.trim()) {
    errors.push({ field: 'selectionReason', message: 'Vendor selection reason is required' });
  }

  return errors;
}

export interface FormValidationErrors {
  basicDetails: ValidationError[];
  thirdPartyRisk: ValidationError[];
  commercialDueDiligence: ValidationError[];
  bids: ValidationError[];
}

/**
 * Get all validation errors for the entire form
 * Only validates forms that are shown based on workflow hierarchy:
 * - Basic Details: ALWAYS shown
 * - Risk Profile: Shown in SHORT and FULL workflows (< $1k + needs contract, OR >= $1k OR needs contract)
 * - Due Diligence: Shown in FULL workflow only (>= $1k OR needs contract)
 * - Competitive Bidding: Shown in FULL workflow only (>= $1k OR needs contract)
 */
export function validateAllForms(
  basicDetails: BasicDetailsData,
  thirdPartyRisk: ThirdPartyRiskData,
  commercialDueDiligence: CommercialDueDiligenceData,
  bids: BidsData
): FormValidationErrors {
  const amount = parseFloat(basicDetails.estimatedContractAmount) || 0;
  const needsIronclad = basicDetails.needsContractorAgreement || false;
  const requiresAccounting = amount >= 1000;
  const showFamilial = amount >= 1000;
  
  // Determine which forms are shown based on workflow
  const shortMode = needsIronclad && !requiresAccounting; // < $1k + needs contract
  const showRiskProfile = shortMode || requiresAccounting || needsIronclad; // SHORT or FULL
  const showDueDiligenceAndBids = requiresAccounting || needsIronclad; // FULL only
  
  return {
    basicDetails: validateBasicDetails(basicDetails, needsIronclad),
    // Only validate Risk Profile if it's shown
    thirdPartyRisk: showRiskProfile ? validateThirdPartyRisk(
      thirdPartyRisk,
      needsIronclad,
      showFamilial
    ) : [],
    // Only validate Due Diligence if it's shown
    commercialDueDiligence: showDueDiligenceAndBids ? validateCommercialDueDiligence(
      commercialDueDiligence,
      basicDetails.estimatedContractAmount
    ) : [],
    // Only validate Competitive Bidding if it's shown
    bids: showDueDiligenceAndBids ? validateBids(bids) : [],
  };
}

/**
 * Check if there are any validation errors across all forms
 */
export function hasValidationErrors(validationResults: FormValidationErrors): boolean {
  return Object.values(validationResults).some(errors => errors.length > 0);
}

/**
 * Get a summary of all validation errors
 */
export function getValidationSummary(validationResults: FormValidationErrors): string {
  const messages: string[] = [];
  
  if (validationResults.basicDetails?.length > 0) {
    messages.push(`Basic Details: ${validationResults.basicDetails.length} error(s)`);
  }
  if (validationResults.thirdPartyRisk?.length > 0) {
    messages.push(`Vendor Risk Profile: ${validationResults.thirdPartyRisk.length} error(s)`);
  }
  if (validationResults.commercialDueDiligence?.length > 0) {
    messages.push(`Commercial Due Diligence: ${validationResults.commercialDueDiligence.length} error(s)`);
  }
  if (validationResults.bids?.length > 0) {
    messages.push(`Vendor Competitive Bidding: ${validationResults.bids.length} error(s)`);
  }

  return messages.join(', ');
}
